import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Home as HomeIcon, Ear, Loader2, WifiOff } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import useScopaOnline from '../multiplayer/useScopaOnline';
import { ConnectionProblem } from '../components/ConnectionProblem';
import { getCaptureOptions } from '../game/captureOptions';
import { cardLabel, cardsLabel } from '../game/cards';
import audioManager from '../audio/audioManager';
import useOnlineNarration from '../multiplayer/useOnlineNarration';
import NeapolitanCard from '../components/NeapolitanCard';
import ScorePanel from '../components/ScorePanel';
import CaptureChoice from '../components/CaptureChoice';
import { PlayersRow, MultiScorePanel, MultiBreakdown, CloseGameControl } from '../components/OnlinePlayers';
import { isMulti, playerName, sideLabel, teamNames, orderedSides, toName, joinNames } from '../multiplayer/multiLabels';

// seat A drives the engine's 'player' actor, seat B the 'cpu' actor. Used only
// to read the (raw) end-of-hand breakdown from the server view.
const KEY = { A: 'player', B: 'cpu' };

function TurnIndicator({ view }) {
  let text = 'È il tuo turno';
  if (view.phase === 'gameOver') text = 'Partita finita';
  else if (view.phase === 'handOver') text = 'Fine mano';
  else if (view.turn !== view.you) text = isMulti(view) ? `Tocca ${toName(playerName(view, view.turn))}` : 'Turno dell’avversario';
  const active = view.phase === 'playing' && view.turn === view.you;
  return (
    <div
      className={`px-3 py-1 rounded-full text-xs font-sans font-semibold tracking-wide ${
        active ? 'bg-[#D4AF37] text-[#14452F]' : 'bg-black/30 text-[#E0E0D1]'
      }`}
      data-testid="online-turn-indicator"
    >
      {text}
    </div>
  );
}

function rematchTextMulti(view, oppGone) {
  const r = view.rematch || {};
  const n = view.numPlayers;
  if (r.declinedBy) return r.declinedBy === 'you' ? null : `${playerName(view, r.declinedBySeat)} ha rifiutato la rivincita.`;
  if (oppGone) return `${awayNames(view)} ha lasciato la partita.`;
  const k = (r.accepted || []).length;
  if (r.you) return `In attesa degli altri giocatori: ${k} su ${n} hanno accettato la rivincita…`;
  if (k > 0) return `${k} su ${n} giocatori vogliono la rivincita!`;
  return null;
}

function awaySeats(view) {
  return (view.players || []).filter((p) => p.seat !== view.you && !p.online).map((p) => p.seat);
}

function awayNames(view, seats = awaySeats(view)) {
  return joinNames(seats.map((s) => playerName(view, s)));
}

// Neutral wording, singular/plural.
function awayText(view, seats) {
  const names = awayNames(view, seats);
  return seats.length > 1
    ? `${names} hanno perso la connessione. La partita riprende al loro rientro.`
    : `${names} ha perso la connessione. La partita riprende al suo rientro.`;
}

function RematchStatus({ view, oppGone }) {
  const r = view.rematch || {};
  let text = null;
  if (isMulti(view)) text = rematchTextMulti(view, oppGone);
  else if (r.declinedBy === 'opponent') text = 'L’avversario ha rifiutato la rivincita.';
  else if (oppGone) text = 'L’avversario ha lasciato la partita.';
  else if (r.you && !r.opponent) text = 'In attesa che l’avversario accetti la rivincita…';
  else if (r.opponent) text = 'L’avversario vuole la rivincita!';
  if (!text) return null;
  return (
    <p className="font-sans text-sm text-[#F2D675] mb-4" data-testid="online-rematch-status">
      {text}
    </p>
  );
}

function closedText(view) {
  const c = view.closedBy || { absent: [] };
  const who = c.by === view.you ? 'Hai chiuso la partita' : `${playerName(view, c.by)} ha chiuso la partita`;
  const verb = c.absent.length > 1 ? 'hanno perso' : 'ha perso';
  return `${who} perché ${awayNames(view, c.absent)} ${verb} la connessione per più di 2 minuti.`;
}

function gameOverTitleMulti(view) {
  if (view.winner === view.yourSide) return view.numPlayers === 4 ? 'La tua squadra ha vinto!' : 'Hai vinto!';
  return view.numPlayers === 4 ? 'Ha vinto la squadra avversaria' : `Ha vinto ${playerName(view, view.winner)}`;
}

function BreakdownRow({ label, p, c, pWon, cWon }) {
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-white/5">
      <span className={`font-sans ${pWon ? 'text-[#D4AF37] font-bold' : 'text-[#E0E0D1]'}`}>{p}</span>
      <span className="font-sans text-xs uppercase tracking-[0.1em] text-[#E0E0D1]/60">{label}</span>
      <span className={`font-sans ${cWon ? 'text-[#D4AF37] font-bold' : 'text-[#E0E0D1]'}`}>{c}</span>
    </div>
  );
}

export default function OnlineGameScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const room = params.get('room');
  const { settings } = useSettings();

  const { connected, view, error, errorSeq, send, tokenRef, offline, retry } = useScopaOnline(room);
  const { live, status, readTable } = useOnlineNarration(view, settings);

  const [selectedCardId, setSelectedCardId] = useState(null);
  const [captureOptions, setCaptureOptions] = useState(null);
  const [pending, setPending] = useState(false);
  const moveSeq = useRef(0);
  const sentAtVersion = useRef(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  const handRef = useRef(null);
  const nextHandBtnRef = useRef(null);
  const homeBtnRef = useRef(null);
  const rematchBtnRef = useRef(null);
  const newGameBtnRef = useRef(null);
  const prevPhaseRef = useRef(null);

  // Opponent move visual sequence (multiplayer). Purely cosmetic: driven by the
  // events the server already sends (played card + captured cards). It never
  // touches game state, telecronaca, live regions or focus.
  const [oppFx, setOppFx] = useState(null);
  const fxVersion = useRef(null);
  const fxTimer = useRef(null);
  useEffect(() => () => clearTimeout(fxTimer.current), []);

  const clearSelection = useCallback(() => {
    setSelectedCardId(null);
    setCaptureOptions(null);
  }, []);

  // Clear the pending lock + any stale local selection once the authoritative
  // version advances past the move we sent. All telecronaca lives in the
  // narration hook, driven by server-forwarded events (verbatim CPU port).
  useEffect(() => {
    if (!view) return;
    if (sentAtVersion.current != null && view.version > sentAtVersion.current) {
      sentAtVersion.current = null;
      setPending(false);
    }
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view && view.version]);

  // Detect an OPPONENT capture on each new authoritative version and play the
  // visual sequence: played+captured shown together (giocata + presa), then the
  // group sweeps toward the opponent (raccolta). Deduped by version so a
  // reconnect / re-sync never replays it. Discards are left as-is (the played
  // card simply appears on the table).
  useEffect(() => {
    if (!view) return;
    if (fxVersion.current != null && view.version <= fxVersion.current) return;
    fxVersion.current = view.version;
    const events = view.events;
    if (!events || !events.length) return;
    const myKey = isMulti(view) ? view.you : KEY[view.you];
    const cap = events.find((e) => e.type === 'capture' && e.actor !== myKey);
    if (cap && cap.played && Array.isArray(cap.captured)) {
      clearTimeout(fxTimer.current);
      setOppFx({ played: cap.played, captured: cap.captured });
      // Hold (entrance + pause), then remove to trigger the sweep-out exit.
      fxTimer.current = setTimeout(() => setOppFx(null), 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view && view.version]);

  const myTurn = !!view && view.phase === 'playing' && view.turn === view.you && !pending;

  const play = useCallback(
    (cardId, captureIds) => {
      const v = viewRef.current;
      if (!v) return;
      moveSeq.current += 1;
      const moveId = `${v.you}-${moveSeq.current}-${Date.now()}`;
      const ok = send({
        type: 'move',
        token: tokenRef.current,
        moveId,
        expectedVersion: v.version,
        cardId,
        captureIds,
      });
      if (ok) {
        sentAtVersion.current = v.version;
        setPending(true);
      }
    },
    [send, tokenRef]
  );

  const selectCard = useCallback(
    (cardId) => {
      const v = viewRef.current;
      if (!v || v.phase !== 'playing' || v.turn !== v.you || pending) {
        audioManager.error();
        status('Non è il tuo turno.');
        return;
      }
      const card = v.yourHand.find((c) => c.id === cardId);
      if (!card) return;
      // Toggle off if re-selecting the pending card.
      if (selectedCardId === cardId && captureOptions) {
        audioManager.deselect();
        clearSelection();
        status(`${cardLabel(card)}, deselezionata.`);
        return;
      }
      const options = getCaptureOptions(card, v.table);
      if (options.length === 0) {
        // No valid capture -> simply play the card.
        audioManager.playCard();
        play(cardId, []);
        clearSelection();
      } else if (options.length === 1) {
        // Forced / single capture -> execute immediately.
        play(cardId, options[0].map((c) => c.id));
        clearSelection();
      } else {
        // Multiple combinations -> let the player choose.
        audioManager.select();
        setSelectedCardId(cardId);
        setCaptureOptions(options);
        const list = options.map((o) => cardsLabel(o)).join('. ');
        status(`${cardLabel(card)}, selezionata. Prese disponibili: ${list}.`);
      }
    },
    [pending, selectedCardId, captureOptions, clearSelection, play, status]
  );

  const chooseCapture = useCallback(
    (index) => {
      if (!captureOptions || !selectedCardId) return;
      const option = captureOptions[index];
      if (!option) {
        audioManager.error();
        status('Selezione non valida.');
        return;
      }
      play(selectedCardId, option.map((c) => c.id));
      clearSelection();
    },
    [captureOptions, selectedCardId, play, clearSelection, status]
  );

  const requestNextHand = useCallback(() => {
    const v = viewRef.current;
    if (isMulti(v) && v.nextHand?.you) return; // already confirmed (server dedupes too)
    if (send({ type: 'nextHand', token: tokenRef.current }) && isMulti(v)) {
      status('Hai confermato. In attesa degli altri giocatori.');
    }
  }, [send, tokenRef, status]);

  const requestRematch = useCallback(() => {
    const v = viewRef.current;
    if (!v || v.phase !== 'gameOver' || v.rematch?.you || v.rematch?.declinedBy) return;
    send({ type: 'rematch', token: tokenRef.current, accept: true });
  }, [send, tokenRef]);

  // Leaving from the game-over dialog declines the rematch so the opponent is told.
  const leaveGameOver = useCallback(
    (to) => {
      const v = viewRef.current;
      if (v && v.phase === 'gameOver' && !v.rematch?.declinedBy) {
        send({ type: 'rematch', token: tokenRef.current, accept: false });
      }
      navigate(to);
    },
    [send, tokenRef, navigate]
  );

  // Opponent considered gone only after a short grace period, so a quick
  // reload/reconnect at gameOver doesn't trigger a false "ha lasciato".
  const [oppGone, setOppGone] = useState(false);
  const atGameOver = !!view && view.phase === 'gameOver';
  const oppOffline = atGameOver && view.opponentOnline === false;
  useEffect(() => {
    if (!oppOffline) {
      setOppGone(false);
      return undefined;
    }
    const t = setTimeout(() => setOppGone(true), 4000);
    return () => clearTimeout(t);
  }, [oppOffline]);

  // 3-4 players: if someone drops mid-game, tell the others (after a 4s grace
  // period so quick reconnects stay silent) instead of leaving them waiting.
  const multi = isMulti(view);
  const awayKey = multi && view.phase !== 'gameOver' ? awaySeats(view).join('') : '';
  const [away, setAway] = useState([]);
  const announcedAway = useRef([]);
  useEffect(() => {
    const now = awayKey.split('').filter(Boolean);
    const back = announcedAway.current.filter((s) => !now.includes(s));
    if (back.length) {
      announcedAway.current = announcedAway.current.filter((s) => now.includes(s));
      setAway(announcedAway.current);
      const v = viewRef.current;
      status(
        announcedAway.current.length === 0 && now.length === 0
          ? 'Tutti i giocatori sono di nuovo collegati.'
          : `${awayNames(v, back)} di nuovo in partita.`
      );
    }
    const fresh = now.filter((s) => !announcedAway.current.includes(s));
    if (!fresh.length) return undefined;
    const t = setTimeout(() => {
      announcedAway.current = [...announcedAway.current, ...fresh];
      setAway(announcedAway.current);
      status(awayText(viewRef.current, fresh));
    }, 4000);
    return () => clearTimeout(t);
  }, [awayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3-4 players: "Chiudi la partita" once someone has been offline for the
  // server's grace period (seeded from the server's offlineMs, re-checked by it).
  const [closeReady, setCloseReady] = useState(false);
  const [closeCheck, setCloseCheck] = useState(0);
  useEffect(() => {
    setCloseReady(false);
    if (!awayKey) return undefined;
    const v = viewRef.current;
    const left = Math.min(
      ...v.players.filter((p) => p.seat !== v.you && !p.online).map((p) => (v.closeGraceMs || 120000) - (p.offlineMs || 0))
    );
    const t = setTimeout(() => setCloseReady(true), Math.max(0, left) + 250);
    return () => clearTimeout(t);
  }, [awayKey, closeCheck]);
  const requestClose = useCallback(() => {
    send({ type: 'closeGame', token: tokenRef.current });
  }, [send, tokenRef]);
  // Server refused (player came back / not long enough): say so and re-arm.
  useEffect(() => {
    if (error !== 'close_not_allowed') return;
    status('Non è possibile chiudere la partita: il giocatore è rientrato o è disconnesso da troppo poco.');
    setCloseCheck((n) => n + 1);
  }, [errorSeq]); // eslint-disable-line react-hooks/exhaustive-deps
  const canClose = multi && closeReady && !!awayKey && view?.status !== 'abandoned';

  // Closed by the players: announce once and move focus into the dialog.
  const closedHomeRef = useRef(null);
  const closedBy = multi && view?.status === 'abandoned' ? view.closedBy : null;
  useEffect(() => {
    if (!closedBy) return;
    status(closedText(viewRef.current));
    closedHomeRef.current?.focus();
  }, [!!closedBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a decline the focused Rivincita button disappears: keep focus in the dialog.
  const declinedByOpp = atGameOver && view.rematch?.declinedBy === 'opponent';
  useEffect(() => {
    if (declinedByOpp) newGameBtnRef.current?.focus();
  }, [declinedByOpp]);

  // Rematch / presence announcements (status channel), only on transitions.
  const rematchSig = useRef(null);
  useEffect(() => {
    if (!view || view.phase !== 'gameOver') {
      rematchSig.current = null;
      return;
    }
    const r = view.rematch || {};
    const cur = { opp: !!r.opponent, you: !!r.you, declined: r.declinedBy, online: !oppGone, k: (r.accepted || []).length };
    const prev = rematchSig.current;
    rematchSig.current = cur;
    if (!prev) return;
    if (isMulti(view)) {
      if (cur.declined === 'opponent' && prev.declined !== 'opponent') status(`${playerName(view, r.declinedBySeat)} ha rifiutato la rivincita.`);
      else if (!cur.online && prev.online && !cur.declined) status(`${awayNames(view)} ha lasciato la partita.`);
      else if (cur.online && !prev.online && !cur.declined) status('Tutti i giocatori sono di nuovo collegati.');
      else if (cur.you && !prev.you) status('Rivincita richiesta. In attesa degli altri giocatori.');
      else if ((r.accepted || []).length > (prev.k || 0) && !cur.you) status(`${(r.accepted || []).length} su ${view.numPlayers} giocatori vogliono la rivincita.`);
      rematchSig.current.k = (r.accepted || []).length;
      return;
    }
    if (cur.declined === 'opponent' && prev.declined !== 'opponent') status('L’avversario ha rifiutato la rivincita.');
    else if (!cur.online && prev.online && !cur.declined) status('L’avversario ha lasciato la partita.');
    else if (cur.online && !prev.online && !cur.declined) status('L’avversario è tornato.');
    else if (cur.opp && !prev.opp) status('L’avversario ha accettato la rivincita.');
    else if (cur.you && !prev.you) status('Rivincita richiesta. In attesa dell’avversario.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view && view.version, oppGone]);

  // Focus management for the dialogs (parity with the offline board).
  useEffect(() => {
    if (!view) return;
    if (view.phase === 'handOver') nextHandBtnRef.current?.focus();
    else if (view.phase === 'gameOver') (rematchBtnRef.current || homeBtnRef.current)?.focus();
    else if (prevPhaseRef.current === 'gameOver') handRef.current?.focus();
    prevPhaseRef.current = view.phase;
  }, [view?.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevCaptureOptions = useRef(null);
  useEffect(() => {
    if (prevCaptureOptions.current && !captureOptions) handRef.current?.focus();
    prevCaptureOptions.current = captureOptions;
  }, [captureOptions]);

  const trapTab = (e) => {
    if (e.key !== 'Tab') return;
    const focusables = e.currentTarget.querySelectorAll(
      'button, [href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!room) {
    navigate('/online', { replace: true });
    return null;
  }

  // ---- Loading / connecting ----
  if (!view) {
    return (
      <div className="relative h-[100dvh] w-full felt-bg flex flex-col items-center justify-center gap-4 px-5" data-testid="online-game-loading">
        {offline && error !== 'room_full' ? (
          <div className="w-full max-w-md">
            <ConnectionProblem onRetry={retry} />
          </div>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" />
            <p className="font-sans text-sm text-[#FDFBF7]" role="status" aria-live="polite">
              {error === 'room_full' ? 'Stanza piena.' : 'Connessione alla partita…'}
            </p>
          </>
        )}
        <button
          type="button"
          onClick={() => navigate('/')}
          data-testid="online-game-home-btn"
          className="rounded-full bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans font-semibold px-5 py-2 transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
        >
          Torna alla Home
        </button>
      </div>
    );
  }

  const scoreState = { matchScores: { player: view.scores.you, cpu: view.scores.opponent } };
  const youName = (view.names && view.names.you) || 'Tu';
  const oppName = (view.names && view.names.opponent) || 'Avversario';
  const b = view.lastHandBreakdown;
  const myKey = KEY[view.you];
  const oppKey = view.you === 'A' ? 'cpu' : 'player';
  const abandoned = view.status === 'abandoned';
  const overlayOpen = !!captureOptions || view.phase === 'handOver' || view.phase === 'gameOver' || (multi && abandoned);

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden felt-bg flex justify-center" data-testid="online-game-screen">
      <div className="sr-only" aria-live="polite" role="status" data-testid="online-live-region">
        {live}
      </div>

      {/* Opponent capture sequence — cosmetic only, aria-hidden, no pointer/focus */}
      <AnimatePresence>
        {oppFx && (
          <motion.div
            className="pointer-events-none absolute left-0 right-0 top-[38%] z-30 flex justify-center px-4"
            aria-hidden="true"
            data-testid="opponent-capture-fx"
            initial={{ opacity: 0, y: -110, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -160, scale: 0.7 }}
            transition={{ type: 'tween', ease: 'easeOut', duration: 0.35 }}
          >
            <div className="flex items-center gap-2 rounded-2xl bg-black/45 backdrop-blur-[2px] px-3 py-2 shadow-[0_10px_24px_rgba(0,0,0,0.5)]">
              <div className="rounded-lg ring-2 ring-[#D4AF37]">
                <NeapolitanCard card={oppFx.played} sizeClass="w-14 sm:w-16" />
              </div>
              <span className="text-[#D4AF37] text-2xl font-bold px-0.5" aria-hidden="true">+</span>
              <div className="flex items-center gap-1.5">
                {oppFx.captured.map((c) => (
                  <NeapolitanCard key={c.id} card={c} sizeClass="w-14 sm:w-16" />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full max-w-md flex flex-col h-full px-3 pt-3 pb-2" inert={overlayOpen ? true : undefined}>
        {/* Top bar */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <button
            type="button"
            onClick={() => navigate('/')}
            data-testid="online-exit-btn"
            aria-label="Esci dalla partita online"
            className="w-9 h-9 rounded-full bg-[#2C1A14] border border-[#D4AF37]/30 flex items-center justify-center text-[#FDFBF7] transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
          >
            <HomeIcon className="w-4 h-4" />
          </button>
          <TurnIndicator view={view} />
          <div className="flex items-center gap-1" data-testid="online-conn">
            {!connected && <WifiOff className="w-4 h-4 text-red-400" aria-label="Disconnesso" />}
          </div>
        </div>

        {!connected && offline && (
          <div className="mb-2">
            <ConnectionProblem onRetry={retry} compact />
          </div>
        )}
        {!connected && !offline && (
          <div className="mb-2 text-center rounded-lg bg-black/40 py-1.5 text-xs text-[#F2D675]" role="status" data-testid="online-reconnecting">
            Riconnessione in corso…
          </div>
        )}

        {away.length > 0 && (
          <div className="mb-2 text-center rounded-lg bg-black/40 py-1.5 px-2 text-xs text-[#F2D675]" data-testid="online-player-away">
            {awayText(view, away)}
            {canClose && <div><CloseGameControl onConfirm={requestClose} /></div>}
          </div>
        )}

        {multi ? (
          <>
            <MultiScorePanel view={view} />
            <PlayersRow view={view} />
          </>
        ) : (
        <>
        <ScorePanel state={scoreState} youLabel={youName} opponentLabel={oppName} />

        {/* Opponent hand (count only — the real cards never reach the client) */}
        <div className="mt-3 flex items-center justify-center gap-1.5" aria-label={`${oppName} ha ${view.opponentCardCount} carte in mano`} data-testid="online-opponent-hand">
          {Array.from({ length: view.opponentCardCount }).map((_, i) => (
            <NeapolitanCard key={i} faceDown sizeClass="w-11 sm:w-12" className="opacity-95" testId={`online-opponent-card-${i}`} />
          ))}
          {view.opponentCardCount === 0 && <div className="h-16" aria-hidden="true" />}
        </div>
        </>
        )}

        {/* Table */}
        <div className="flex-1 relative flex flex-col items-center justify-center my-2">
          <div className="w-full flex flex-wrap items-center justify-center gap-2 content-center min-h-[7rem] rounded-2xl bg-black/10 p-3 border border-[#D4AF37]/10" data-testid="online-table-area" aria-label="Tavolo da gioco">
            <AnimatePresence>
              {view.table.length === 0 ? (
                <motion.p key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-sans text-sm text-[#E0E0D1]/50 italic">
                  Tavolo vuoto
                </motion.p>
              ) : (
                view.table.map((c) => (
                  <motion.div key={c.id} aria-hidden="true" layout initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, transition: { duration: 0.12 } }}>
                    <NeapolitanCard card={c} sizeClass="w-14 sm:w-16" testId={`online-table-card-${c.id}`} />
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>

          {settings.accessibleMode && (
            <div
              role="text"
              tabIndex={0}
              onClick={readTable}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  readTable();
                }
              }}
              data-testid="online-read-table-btn"
              aria-label="Leggi tavolo"
              className="mt-3 flex items-center gap-2 rounded-full bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans font-semibold text-sm px-4 py-2 cursor-pointer transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60"
            >
              <Ear className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" />
              Leggi tavolo
            </div>
          )}
        </div>

        {/* Player hand */}
        <div ref={handRef} tabIndex={-1} className="flex items-end justify-center gap-2 sm:gap-3 pb-1 min-h-[6.5rem] outline-none" data-testid="online-player-hand" aria-label="La tua mano">
          {view.yourHand.map((c) => (
            <div key={c.id}>
              <NeapolitanCard
                card={c}
                interactive
                disabled={!myTurn}
                selected={selectedCardId === c.id}
                onClick={() => selectCard(c.id)}
                sizeClass="w-16 sm:w-20"
                testId={`online-player-card-${c.id}`}
              />
            </div>
          ))}
          {view.yourHand.length === 0 && (
            <p className="font-sans text-sm text-[#E0E0D1]/50 italic pb-6">In attesa delle carte…</p>
          )}
        </div>
      </div>

      {/* Capture choice */}
      <AnimatePresence>
        {captureOptions && (
          <CaptureChoice options={captureOptions} onChoose={chooseCapture} onCancel={clearSelection} />
        )}
      </AnimatePresence>

      {/* Hand over modal */}
      <AnimatePresence>
        {view.phase === 'handOver' && b && !(multi && abandoned) && (
          <motion.div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm p-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="online-hand-over-modal">
            <motion.div role="dialog" aria-modal="true" aria-labelledby="online-hand-over-title" onKeyDown={trapTab} className="w-full max-w-sm rounded-2xl bg-[#1c2a20] border border-[#D4AF37]/30 p-5" initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
              <h2 id="online-hand-over-title" className="font-serif text-2xl text-[#FDFBF7] text-center mb-1">Fine della mano</h2>
              {multi ? (
                <>
                  <div className="flex items-center justify-around my-3" data-testid="online-hand-over-scores">
                    {orderedSides(view).map((s) => (
                      <div key={s} className="text-center min-w-0">
                        <p className="text-xs uppercase tracking-wider text-[#E0E0D1]/70 truncate">{sideLabel(view, s)}</p>
                        <p className={`font-serif text-3xl font-bold ${s === view.yourSide ? 'text-[#D4AF37]' : 'text-[#FDFBF7]'}`}>{view.sideScores[s]}</p>
                      </div>
                    ))}
                  </div>
                  <MultiBreakdown view={view} />
                </>
              ) : (
              <>
              <div className="flex items-center justify-around my-3">
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wider text-[#E0E0D1]/70">{youName}</p>
                  <p className="font-serif text-4xl font-bold text-[#D4AF37]">{view.scores.you}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wider text-[#E0E0D1]/70">{oppName}</p>
                  <p className="font-serif text-4xl font-bold text-[#FDFBF7]">{view.scores.opponent}</p>
                </div>
              </div>
              <div className="mt-2" aria-label="Dettaglio punti della mano">
                <BreakdownRow label="Scope" p={b[myKey].scope} c={b[oppKey].scope} pWon={b[myKey].scope > 0} cWon={b[oppKey].scope > 0} />
                <BreakdownRow label="Carte" p={b[myKey].carte} c={b[oppKey].carte} pWon={b[myKey].won.carte} cWon={b[oppKey].won.carte} />
                <BreakdownRow label="Denari" p={b[myKey].denari} c={b[oppKey].denari} pWon={b[myKey].won.denari} cWon={b[oppKey].won.denari} />
                <BreakdownRow label="Settebello" p={b[myKey].settebello ? 'Sì' : 'No'} c={b[oppKey].settebello ? 'Sì' : 'No'} pWon={b[myKey].won.settebello} cWon={b[oppKey].won.settebello} />
                <BreakdownRow label="Primiera" p={b[myKey].primiera} c={b[oppKey].primiera} pWon={b[myKey].won.primiera} cWon={b[oppKey].won.primiera} />
              </div>
              </>
              )}
              <button
                type="button"
                ref={nextHandBtnRef}
                onClick={requestNextHand}
                aria-disabled={multi && view.nextHand?.you ? true : undefined}
                data-testid="online-next-hand-btn"
                className={`mt-5 w-full rounded-full font-sans font-bold py-3 transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60 ${multi && view.nextHand?.you ? 'bg-[#D4AF37]/50 text-[#14452F]/80 cursor-default' : 'bg-[#D4AF37] text-[#14452F] hover:bg-[#F2D675]'}`}
              >
                {multi
                  ? `${view.nextHand?.you ? 'In attesa degli altri' : 'Continua'} (${(view.nextHand?.confirmed || []).length}/${view.numPlayers})`
                  : 'Continua'}
              </button>
              {multi && away.length > 0 && (
                <div className="mt-3 text-center" data-testid="online-hand-over-away">
                  <p className="font-sans text-xs text-[#F2D675]">{awayText(view, away)}</p>
                  {canClose && <CloseGameControl onConfirm={requestClose} testPrefix="close-game-handover" />}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game over modal */}
      <AnimatePresence>
        {view.phase === 'gameOver' && (
          <motion.div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-md p-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="online-game-over-modal">
            <motion.div role="dialog" aria-modal="true" aria-labelledby="online-game-over-title" onKeyDown={trapTab} className="w-full max-w-sm rounded-2xl bg-[#1c2a20] border border-[#D4AF37]/40 p-6 text-center" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <h2 id="online-game-over-title" className="font-serif text-4xl font-bold text-[#D4AF37] mb-2" data-testid="online-game-over-title">
                {multi ? gameOverTitleMulti(view) : view.winner === view.you ? 'Hai vinto!' : 'Hai perso'}
              </h2>
              <p className="font-sans text-[#E0E0D1] mb-5" data-testid="online-final-score">
                {multi
                  ? `Punteggio finale — ${orderedSides(view).map((s) => `${view.numPlayers === 4 ? teamNames(view, s) : sideLabel(view, s)} ${view.sideScores[s]}`).join(', ')}.`
                  : `Punteggio finale — ${youName} ${view.scores.you}, ${oppName} ${view.scores.opponent}.`}
              </p>
              <RematchStatus view={view} oppGone={oppGone} />
              <div className="flex flex-col gap-3">
                {!view.rematch?.declinedBy && (
                  <button
                    type="button"
                    ref={rematchBtnRef}
                    onClick={requestRematch}
                    aria-disabled={view.rematch?.you ? true : undefined}
                    data-testid="online-rematch-btn"
                    className={`w-full rounded-full font-sans font-bold py-3 transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60 ${view.rematch?.you ? 'bg-[#D4AF37]/50 text-[#14452F]/80 cursor-default' : 'bg-[#D4AF37] text-[#14452F] hover:bg-[#F2D675]'}`}
                  >
                    {view.rematch?.you ? 'Rivincita richiesta…' : 'Rivincita'}
                  </button>
                )}
                <button type="button" ref={newGameBtnRef} onClick={() => leaveGameOver('/online')} data-testid="online-new-game-btn" className="w-full rounded-full bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans font-semibold py-3 transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50">
                  Nuova partita online
                </button>
                <button type="button" ref={homeBtnRef} onClick={() => leaveGameOver('/')} data-testid="online-game-over-home-btn" className="w-full rounded-full bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans font-semibold py-3 transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50">
                  Home
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Abandoned overlay */}
      <AnimatePresence>
        {abandoned && view.phase !== 'gameOver' && (
          <motion.div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="online-abandoned-modal">
            <div role="dialog" aria-modal="true" className="w-full max-w-sm rounded-2xl bg-[#1c2a20] border border-[#D4AF37]/30 p-6 text-center">
              <h2 className="font-serif text-2xl text-[#FDFBF7] mb-2">Partita interrotta</h2>
              <p className="font-sans text-sm text-[#E0E0D1] mb-5" data-testid="online-abandoned-text">
                {closedBy ? closedText(view) : 'La partita è stata chiusa per inattività.'}
              </p>
              <button type="button" ref={closedHomeRef} onClick={() => navigate('/')} data-testid="online-abandoned-home-btn" className="w-full rounded-full bg-[#D4AF37] text-[#14452F] font-sans font-bold py-3 transition-colors hover:bg-[#F2D675] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60">
                Home
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
